<template>
    <div class="table-container w-full">
        <div class="mt-1 flex justify-between lg:flex-row flex-col">
            <div class="w-auto">
                <a-input-search
                    data-cy="templates-input-search"
                    @search="onSearch"
                    :placeholder="$t('globals.tables.search')"
                />
            </div>

            <div v-if="section_type == 'default'" class="pt-4 lg:pt-0">
                <a-dropdown :trigger="['click']" v-if="recordsSelected">
                    <a-button @click="(e) => e.preventDefault()" data-cy="btn-mass-selection">
                        {{ $t('company.templates.table.actions.actions') }}
                        <DownOutlined />
                    </a-button>
                    <template #overlay>
                        <a-menu @click="onActionButtons">
                            <a-menu-item key="delete" data-cy="actions-delete-template">
                                {{ $t('company.templates.table.actions.delete') }}
                            </a-menu-item>
                        </a-menu>
                    </template>
                </a-dropdown>
                <a-button-group>
                    <a-button @click="onAdd" data-cy="templates-create-button" type="primary">
                        {{ $t('company.templates.table.actions.create') }}
                    </a-button>
                </a-button-group>
            </div>
        </div>
        <div class="list-table pt-2 md:pt-4 overflow-x-auto overflow-visible mt-1">
            <div class="w-full">
                <a-table
                    data-cy="templates-table"
                    :columns="getColumns"
                    :dataSource="data"
                    :rowKey="(record) => record.id"
                    :pagination="pagination"
                    :locale="locale"
                    :loading="loading"
                    :rowSelection="
                        section_type == 'default'
                            ? {
                                  selectedRowKeys: selected_row_keys,
                                  onChange: onSelectChange,
                              }
                            : {}
                    "
                    @change="handleTableChange"
                    size="middle"
                    class="ant-table-striped"
                >
                    <template #bodyCell="{ column, record, text }">
                        <template v-if="column.key === 'actions'">
                            <a-button-group>
                                <a-tooltip placement="top" :title="$t('company.templates.table.actions.edit')">
                                    <a-button
                                        data-cy="edit-button"
                                        class="text-blue-400 hover:text-blue-400 focus:text-blue-400"
                                        @click="onTableAction(record.id, 'edit')"
                                        :disabled="recordsSelected"
                                    >
                                        <EditOutlined />
                                    </a-button>
                                </a-tooltip>
                                <a-tooltip placement="top" :title="$t('company.templates.table.actions.delete')">
                                    <a-button
                                        class="text-default-red hover:text-default-red focus:text-default-red"
                                        data-cy="btn-template-delete-single"
                                        @click="onTableAction(record.id, 'delete')"
                                        :disabled="recordsSelected"
                                    >
                                        <DeleteOutlined />
                                    </a-button>
                                </a-tooltip>
                            </a-button-group>
                        </template>
                        <template v-else-if="column.key === 'select'">
                            <a-button data-cy="edit-button" type="primary" @click="onSelect(record)">
                                {{ $t('company.templates.table.actions.select') }}
                            </a-button>
                        </template>
                        <template v-else>
                            {{ text }}
                        </template>
                    </template>
                </a-table>
                <span v-if="data.length > 0" data-cy="page-item-range" class="page-item-range flex">
                    {{
                        $t('globals.tables.page_range', {
                            pi_start: pageItemStart,
                            pi_end: pageItemEnd,
                            total: pagination.total,
                        })
                    }}
                </span>
            </div>
        </div>
    </div>
</template>
<script>
import { DeleteOutlined, DownOutlined, EditOutlined } from '@ant-design/icons-vue';

export default {
    components: { DeleteOutlined, DownOutlined, EditOutlined },
    props: {
        company_id: {
            type: String,
            default: '',
        },
        section_type: {
            type: String,
            default: 'default',
        },
        user_type: {
            type: String,
            default: 'admin',
        },
    },
    data() {
        return {
            data: [],
            selected_row_keys: [],
            search_value: '',
            pagination: {
                pageSize: 10,
                showSizeChanger: false,
                total: 0,
            },
            locale: {
                emptyText: this.$t('globals.tables.no_data'),
            },
            loading: false,
            processing: false,
            current_filter: {},
            sorter: {},
            params: {},
            list_manager_item: {},
            association_id: null,
            association_list: {},
            category: null,
            categories: [],
        };
    },
    mounted() {
        this.fetch();
    },
    computed: {
        multipleRecordsSelected() {
            return this.selected_row_keys.length > 1;
        },
        pageItemStart() {
            let current_page = this.pagination.current;

            if (current_page === undefined) current_page = 1;

            return (current_page - 1) * this.pagination.pageSize + 1;
        },
        pageItemEnd() {
            let current_page = this.pagination.current,
                page_item_end = 0;

            if (current_page === undefined) current_page = 1;

            page_item_end = current_page * this.pagination.pageSize;

            if (this.pagination.total !== undefined && this.pagination.total < page_item_end) {
                return this.pagination.total;
            } else {
                return page_item_end;
            }
        },
        recordsSelected() {
            return this.selected_row_keys.length > 0;
        },
        getColumns() {
            let columns = [];

            if (this.section_type == 'cover') {
                columns = [
                    {
                        title: this.$t('globals.tables.templates.headers.certificate_type'),
                        key: 'certificate_type',
                        dataIndex: 'certificate_type',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.certificate_code'),
                        key: 'certificate_code',
                        dataIndex: 'certificate_code',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_1_label'),
                        key: 'section_1_label',
                        dataIndex: 'section_1_label',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_2_label'),
                        key: 'section_2_label',
                        dataIndex: 'section_2_label',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_3_label'),
                        key: 'section_3_label',
                        dataIndex: 'section_3_label',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_4_label'),
                        key: 'section_4_label',
                        dataIndex: 'section_4_label',
                    },
                    {
                        key: 'select',
                        width: 70,
                    },
                ];
            } else {
                columns = [
                    {
                        key: 'actions',
                        width: 35,
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.certificate_type'),
                        key: 'certificate_type',
                        dataIndex: 'certificate_type',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.certificate_code'),
                        key: 'certificate_code',
                        dataIndex: 'certificate_code',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_1_label'),
                        key: 'section_1_label',
                        dataIndex: 'section_1_label',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_1'),
                        key: 'section_1',
                        dataIndex: 'section_1',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_2_label'),
                        key: 'section_2_label',
                        dataIndex: 'section_2_label',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_2'),
                        key: 'section_2',
                        dataIndex: 'section_2',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_3_label'),
                        key: 'section_3_label',
                        dataIndex: 'section_3_label',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_3'),
                        key: 'section_3',
                        dataIndex: 'section_3',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_4_label'),
                        key: 'section_4_label',
                        dataIndex: 'section_4_label',
                    },
                    {
                        title: this.$t('globals.tables.templates.headers.section_4'),
                        key: 'section_4',
                        dataIndex: 'section_4',
                    },
                ];
            }
            return columns;
        },
    },
    methods: {
        fetch(params = {}) {
            if (this.company_id !== '' && this.company_id !== undefined && this.company_id !== null) {
                this.loading = true;
                this.params = params;
                let url = '';

                if (this.user_type == 'admin') {
                    url = '/api/companies/' + this.company_id + '/templates';
                } else {
                    url = '/api/general/companies/' + this.company_id + '/templates';
                }

                this.$http
                    .get(url, { params: params })
                    .then((response) => {
                        const pagination = { ...this.pagination };

                        // Read total count from server
                        pagination.total = response.data.meta.total_count;

                        this.data = response.data.data;
                        this.pagination = pagination;
                        this.loading = false;
                    })
                    .catch((error) => {
                        if (error.response.status == 403) {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.403_mod'),
                            });
                        } else {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVLMIF017' }),
                            });
                        }
                    });
            }
        },
        reload() {
            const filters = this.current_filter;

            this.fetch({
                ...this.params,
                results: this.pagination.pageSize,
                page: this.pagination.current,
                sortField: this.sorter.field,
                sortOrder: this.sorter.order,
                ...filters,
            });
        },
        onSelectChange(selected_row_keys, selected_rows) {
            this.selected_row_keys = selected_row_keys;
        },
        onTableAction(id, key) {
            switch (key) {
                case 'edit':
                    this.$emit('onTableAction', {
                        id: id,
                        key: key,
                    });
                    break;
                case 'delete':
                    this.onDelete(id);
                    break;
            }
        },
        handleTableChange(pagination, filters, sorter) {
            const page = { ...this.pagination };

            page.current = pagination.current;

            this.pagination = page;
            this.current_filter = filters;
            this.sorter = sorter;

            this.fetch({
                ...this.params,
                results: pagination.pageSize,
                page: pagination.current,
                sortField: sorter.field,
                sortOrder: sorter.order,
                ...filters,
            });
        },
        onSearch(input) {
            let regex = /^[a-zA-Z0-9\s\(\)_.-]+$/;

            if (regex.test(input) || input === '') {
                if (input === '') {
                    this.search_value = null;
                } else {
                    this.search_value = input;
                }

                this.onSelectChange([]);

                this.fetch({
                    search_value: this.search_value,
                });
                this.pagination.current = 1;
            } else {
                this.$notification.error({
                    message: this.$t('globals.tables.invalid_characters'),
                });
            }
        },
        onAdd() {
            this.$emit('onAdd');
        },
        onActionButtons(event, type) {
            switch (event.key) {
                case 'delete':
                    this.onDelete();
                    break;
            }
        },
        onDelete(item_id = '') {
            let ids = [];

            if (item_id !== '' && item_id !== undefined) {
                ids = [item_id];
            } else if (this.selected_row_keys.length >= 1) {
                ids = this.selected_row_keys;
            }

            let that = this;

            this.$confirm({
                title: this.$t('company.templates.confirm.delete'),
                content: '',
                onOk() {
                    that.$http
                        .post('/api/templates/delete', {
                            ids: ids,
                        })
                        .then((response) => {
                            if (response.data.status == 'success') {
                                that.$notification.success({
                                    message: response.data.message,
                                });

                                that.selected_row_keys = [];

                                that.reload();
                            } else {
                                that.$notification.error({
                                    message: response.data.message,
                                });
                            }
                        })
                        .catch((error) => {
                            this.$notification.error({
                                message: this.$t('globals.global.notifications.error.any', { code: 'CVLMID0144' }),
                            });
                        })
                        .finally(() => {
                            that.processing = false;
                        });
                },
            });
        },
        onSelect(record) {
            this.$emit('select', record);
        },
    },
};
</script>
